/**
 * Helpers for the in-app "Install app" affordance. Kept dependency-free and
 * side-effect-free so they can be unit tested with a mocked `window`/`navigator`.
 */

/**
 * The non-standard `beforeinstallprompt` event fired by Chromium browsers when
 * the app meets the install criteria. Not part of the DOM lib types.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** True when the app is already running as an installed PWA (standalone). */
export function isStandalonePwa(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Legacy iOS Safari exposes standalone mode here instead of display-mode.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Home-screen iOS PWA (Safari), not a Capacitor WebView. */
export function isIosStandalonePwa(): boolean {
  return isIosDevice() && isStandalonePwa();
}

/**
 * True on iPhone / iPad / iPod, including iPadOS which reports a desktop
 * `MacIntel` platform but has a touch screen.
 */
export function isIosDevice(): boolean {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}
